import React, { useContext, useEffect } from "react";
import { LocalKeys, useTranslation } from "utilities/i18n";
import {
  Link,
  NavLink,
  Redirect,
  Route,
  RouteComponentProps,
  SelectedTenant,
  Switch,
  useQueryParam
} from "polyrouter";
import { Layout } from "views/Main/Layouts";
import GeneralSettings from "routes/Admin/Accounts/Settings/GeneralSettings";
import ProvisioningSettings from "routes/Admin/Accounts/Settings/ProvisioningSettings";
import DesktopSettings from "routes/Admin/Accounts/Desktop/Desktop";
import "./AccountDetails.scss";
import AccountDetails from "./AccountDetails";
import Members from "routes/Admin/Accounts/Members/Members";
import { ApiIntegration } from "routes/Admin/Accounts/Integrations/ApiIntegration";
import DirectoryIntegration from "routes/Admin/Accounts/Integrations/DirectoryIntegration";
import { useFeatureFlagWithTenant } from "elements";
import { RelaySettings } from "../Relay/Relay";
import TenantNotificationCenter from "../Notifications/TenantNotificationCenter";
import TenantTeams from "../Notifications/Teams/TenantTeams";
import ServiceNow from "../Notifications/ServiceNow/TenantServiceNow";
import { ProtectedLink, ProtectedNavLink, ProtectedRoute } from "TsRoleGuards";
import { AuditLogs } from "../AuditLogs/AuditLogs";
import {
  placeholderTenant,
  SelectedTenantContext
} from "polyrouter/SelectedTenantProvider";

export const AccountDetailsRouter: React.FC<RouteComponentProps<{
  id: string;
}>> = ({ match, location }) => {
  const { t } = useTranslation();
  const tenantId = match.params.id;
  const [activeTenant, setActive] = useQueryParam<SelectedTenant>("tenantId");

  useEffect(() => {
    // if the active tenant is not the account in view, set it as the active tenant
    if (activeTenant?.toLocaleString() !== tenantId) {
      setActive(tenantId);
    }
  }, [activeTenant, tenantId, setActive]);

  const { name } =
    useContext(SelectedTenantContext).tenant ?? placeholderTenant;

  const isMemberDetailView = location.pathname.includes("members/details");

  let returnToPath = "/admin/accounts";
  let backButtonMessage: LocalKeys = "ACCOUNTS";
  if (isMemberDetailView) {
    returnToPath = `/admin/accounts/details/${tenantId}/members`;
    backButtonMessage = "ACCOUNT_MEMBERS";
  }

  const [isIntegrationsEnabled] = useFeatureFlagWithTenant(
    "INTEGRATION_AZURE_AD"
  );
  const [isEdgeRelayEnabled] = useFeatureFlagWithTenant("EDGE_RELAY");

  const { Sidebar } = Layout;
  return (
    <div className="account-details-content">
      <Layout
        back={{
          to: returnToPath,
          title: t(backButtonMessage),
          iconDesc: t(backButtonMessage)
        }}
      >
        <>
          {!isMemberDetailView && (
            <Sidebar>
              <span className="title-capitalized text-break">{name}</span>
              <ul className="menu router">
                <li>
                  <NavLink nest exact to="">
                    {t("DETAILS")}
                  </NavLink>
                </li>
                <li>
                  <NavLink nest exact to="/general">
                    {t("GENERAL")} {t("SETTINGS")}
                  </NavLink>
                </li>
                <li>
                  <NavLink nest exact to="/members">
                    {t("ACCOUNT_MEMBERS")}
                  </NavLink>
                </li>
                <li>
                  <NavLink nest exact to="/provisioning">
                    {t("PROVISIONING")}
                  </NavLink>
                </li>
                <li>
                  <Link nest to="/notifications">
                    {t("NOTIFICATIONS_MENU")}
                  </Link>
                  <Route nest path="/notifications">
                    <ul className="menu router sub pl-1">
                      <li>
                        <NavLink exact nest to="/email">
                          {t("NOTIFICATIONS_POLY_MENU")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink exact nest to="/teams">
                          {t("MICROSOFT_TEAMS")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink exact nest to="/service-now">
                          {t("SERVICE_NOW")}
                        </NavLink>
                      </li>
                    </ul>
                  </Route>
                </li>
                <li>
                  <ProtectedNavLink
                    nest
                    exact
                    to="/desktop"
                    requirement="UPDATE_TENANT"
                  >
                    {t("POLY_LENS_DESKTOP")}
                  </ProtectedNavLink>
                </li>
                {isIntegrationsEnabled && (
                  <li>
                    <ProtectedLink
                      nest
                      to="/integrations"
                      requirement="UPDATE_TENANT"
                    >
                      {t("INTEGRATIONS")}
                    </ProtectedLink>
                    <Route nest path="/integrations">
                      <ul className="menu router sub pl-1">
                        <li>
                          <NavLink exact nest to="/api">
                            {t("API_INTEGRATION_MENU")}
                          </NavLink>
                        </li>
                        <li>
                          <NavLink exact nest to="/directory">
                            {t("DIRECTORY_INTEGRATION_MENU")}
                          </NavLink>
                        </li>
                      </ul>
                    </Route>
                  </li>
                )}
                {isEdgeRelayEnabled && (
                  <li>
                    <ProtectedNavLink
                      nest
                      exact
                      to="/relay"
                      requirement="UPDATE_TENANT"
                    >
                      {t("RELAY")}
                    </ProtectedNavLink>
                  </li>
                )}
                <li>
                  <NavLink nest exact to="/audit-logs">
                    {t("AUDIT_LOGS")}
                  </NavLink>
                </li>
              </ul>
            </Sidebar>
          )}
          <Switch>
            <Route nest path="/general">
              <GeneralSettings />
            </Route>
            <Route nest path="/members">
              <Members />
            </Route>
            <Route nest path="/provisioning">
              <ProvisioningSettings id={tenantId} />
            </Route>
            <ProtectedRoute
              nest
              path="/notifications"
              requirement="nc:core:readActions"
              redirectUrl={returnToPath}
            >
              <Switch nest>
                <Route nest path="/email">
                  <TenantNotificationCenter tenantId={tenantId} />
                </Route>
                <Route nest path="/teams" component={TenantTeams} />
                <Route nest path="/service-now" component={ServiceNow} />
                <Route>
                  <Redirect nest from="/notifications" to="/email" />
                </Route>
              </Switch>
            </ProtectedRoute>
            <ProtectedRoute nest path="/desktop" requirement="UPDATE_TENANT">
              <DesktopSettings id={tenantId} />
            </ProtectedRoute>
            <ProtectedRoute
              nest
              path="/integrations"
              requirement="UPDATE_TENANT"
            >
              <Switch nest>
                <Route nest path="/api">
                  <ApiIntegration />
                </Route>
                <Route nest path="/directory">
                  <DirectoryIntegration id={tenantId} />
                </Route>
                <Route>
                  <Redirect nest from="/notifications" to="/api" />
                </Route>
              </Switch>
            </ProtectedRoute>
            <ProtectedRoute nest path="/relay" requirement="UPDATE_TENANT">
              <RelaySettings tenantId={tenantId} />
            </ProtectedRoute>
            <Route nest path="/audit-logs">
              <AuditLogs tenantId={tenantId} />
            </Route>
            <Route path="">
              <AccountDetails id={tenantId} />
            </Route>
          </Switch>
        </>
      </Layout>
    </div>
  );
};

export default AccountDetailsRouter;
